import ConditionalToolTip from "components/design/conditionalToolTip";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import RadioGroup from "components/fileTax/FilingRadioGroup";
import { CrossIcon } from "components/icons/CrossIcon";

const ClassificationClassCheckbox = ({
  cls,
  item,
  classes,
  classifications,
  noClassification,
  onValueChange,
}: {
  cls: { uuid: string; name: string };
  item: {
    uuid: string;
    name: string;
    classes: { uuid: string; name: string }[];
  };
  classes: string[];
  classifications: string[];
  noClassification: string[];
  onValueChange: (payload: {
    classification: string[];
    classes: string[];
    noClassification: string[];
  }) => void;
}) => {
  const handleClassCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.stopPropagation();
    if (e.target.checked) {
      const newClasses = [...new Set([...classes, cls.uuid])];
      onValueChange({
        noClassification: [
          ...noClassification.filter((cls) => cls !== item.uuid),
        ],
        classification: [...new Set([...classifications, item.uuid])],
        classes: newClasses,
      });
    } else {
      const newClasses = [
        ...new Set([...classes.filter((cl) => cl !== cls.uuid)]),
      ];
      const allClasses = item.classes.map((c) => c.uuid);
      const isAnyClassChecked = allClasses.some((c) => newClasses.includes(c));
      const newclassification = isAnyClassChecked
        ? classifications
        : classifications.filter((cl) => cl !== item.uuid);
      onValueChange({
        noClassification,
        classes: newClasses,
        classification: newclassification,
      });
    }
  };

  return (
    <Checkbox
      key={cls.uuid}
      label={cls.name}
      name={cls.uuid}
      checked={classes.includes(cls.uuid)}
      onChange={handleClassCheckboxChange}
    />
  );
};

const handleSelectAllChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  item: {
    uuid: string;
    name: string;
    classes: { uuid: string; name: string }[];
  },
  classifications: string[],
  classes: string[],
  noClassification: string[],
  onValueChange: (payload: {
    classification: string[];
    classes: string[];
    noClassification: string[];
  }) => void
) => {
  e.stopPropagation();
  if (e.target.checked) {
    onValueChange({
      classification: [...new Set([...classifications, item.uuid])],
      classes: [
        ...new Set([...classes, ...item.classes.map((cls) => cls.uuid)]),
      ],
      noClassification: noClassification.filter((cls) => cls !== item.uuid),
    });
  } else {
    const currentGroupClassIds = item.classes.map((c) => c.uuid);
    const newClasses = classes.filter(
      (cl) => !currentGroupClassIds.includes(cl)
    );
    onValueChange({
      noClassification,
      classification: classifications.filter((cls) => cls !== item.uuid),
      classes: [...new Set([...newClasses])],
    });
  }
};

const ClassificationRadioGroupContent = ({
  item,
  classifications,
  classes,
  noClassification,
  onValueChange,
}: {
  item: {
    uuid: string;
    name: string;
    classes: { uuid: string; name: string }[];
  };
  classifications: string[];
  classes: string[];
  noClassification: string[];
  onValueChange: (payload: {
    classification: string[];
    classes: string[];
    noClassification: string[];
  }) => void;
}) => {
  const handleSelectGroup = () => {
    onValueChange({
      classification: [...new Set([...classifications, item.uuid])],
      classes: [
        ...new Set([...classes, ...item.classes.map((cls) => cls.uuid)]),
      ],
      noClassification: noClassification.filter((cls) => cls !== item.uuid),
    });
  };

  const handleNoneGroup = () => {
    const currentGroupClassIds = item.classes.map((c) => c.uuid);
    onValueChange({
      noClassification: [...new Set([...noClassification, item.uuid])],
      classification: classifications.filter((cls) => cls !== item.uuid),
      classes: [
        ...new Set([
          ...classes.filter((cl) => !currentGroupClassIds.includes(cl)),
        ]),
      ],
    });
  };

  return (
    <RadioGroup.Root
      onValueChange={(value) => {
        if (value === `SELECT-${item.uuid}`) {
          handleSelectGroup();
          return;
        }
        if (value === `NONE-${item.uuid}`) {
          handleNoneGroup();
          return;
        }
      }}
      value={
        noClassification.includes(item.uuid)
          ? `NONE-${item.uuid}`
          : classifications.includes(item.uuid) &&
            item.classes.length > 0 &&
            item.classes.some((cls) => classes.includes(cls.uuid))
          ? `SELECT-${item.uuid}`
          : ""
      }
    >
      <RadioGroup.Content className="t-flex t-flex-col">
        <RadioGroup.Item
          align="start"
          value={`NONE-${item.uuid}`}
          className="!t-h-auto sm:t-text-left"
        >
          <div>{`No ${item.name} assigned`}</div>
        </RadioGroup.Item>

        <ConditionalToolTip
          condition={
            item.classes.length === 0 && "No classes added for this group yet"
          }
        >
          <span>
            <RadioGroup.Item
              value={`SELECT-${item.uuid}`}
              className="!t-h-auto t-mt-4 t-w-full"
              disabled={item.classes.length === 0}
            >
              <p>Select {item.name}</p>
            </RadioGroup.Item>
            {item.classes.length > 0 && (
              <span
                className="t-flex t-flex-col t-gap-2 t-w-full t-ml-7"
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  label="Select all"
                  name={`${item.uuid}-checkbox-all`}
                  checked={
                    item.classes.every((cls) => classes.includes(cls.uuid)) &&
                    classifications.includes(item.uuid)
                  }
                  indeterminate={
                    item.classes.some((cls) => classes.includes(cls.uuid)) &&
                    !item.classes.every((cls) => classes.includes(cls.uuid))
                  }
                  onChange={(e) =>
                    handleSelectAllChange(
                      e,
                      item,
                      classifications,
                      classes,
                      noClassification,
                      onValueChange
                    )
                  }
                />
                {item.classes.map((cls) => (
                  <div onClick={(e) => e.stopPropagation()} key={cls.uuid}>
                    <ClassificationClassCheckbox
                      key={cls.uuid}
                      cls={cls}
                      item={item}
                      classes={classes}
                      classifications={classifications}
                      noClassification={noClassification}
                      onValueChange={onValueChange}
                    />
                  </div>
                ))}
              </span>
            )}
          </span>
        </ConditionalToolTip>
      </RadioGroup.Content>
    </RadioGroup.Root>
  );
};

export const ClassificationRadioGroup = ({
  classifications = [],
  classes = [],
  noClassification = [],
  classificationData = [] as {
    uuid: string;
    name: string;
    classes: { uuid: string; name: string }[];
  }[],
  onValueChange,
}: {
  classifications: string[];
  classes: string[];
  noClassification: string[];
  classificationData: {
    uuid: string;
    name: string;
    classes: { uuid: string; name: string }[];
  }[];
  onValueChange: (payload: {
    classification: string[];
    classes: string[];
    noClassification: string[];
  }) => void;
}) => {
  const handleResetForGroup = (
    item: {
      uuid: string;
      name: string;
      classes: { uuid: string; name: string }[];
    },
    classifications: string[],
    classes: string[],
    noClassification: string[],
    onValueChange: (payload: {
      classification: string[];
      classes: string[];
      noClassification: string[];
    }) => void
  ) => {
    onValueChange({
      noClassification: Array.isArray(noClassification)
        ? noClassification.filter((cls) => cls !== item.uuid)
        : [],
      classification: Array.isArray(classifications)
        ? classifications.filter((cls) => cls !== item.uuid)
        : [],
      classes: Array.isArray(classes)
        ? classes.filter((cl) => !item.classes.map((c) => c.uuid).includes(cl))
        : [],
    });
  };

  return (
    <div className="t-flex t-flex-col ">
      <Accordion.Root
        type="multiple"
        className="t-flex t-flex-col t-pt-4 t-gap-3 sm:t-pt-0 sm:t-gap-2"
      >
        {classificationData.length > 0 ? (
          classificationData.map((item) => (
            <Accordion.Item
              key={item.uuid}
              value={item.uuid}
              className=" t-mt-10 !t-h-auto "
            >
              <Accordion.Trigger
                defaultValue=""
                className="sm:t-p-3 sm:t-text-subtext-sm"
              >
                <div className="t-flex t-items-center t-justify-between t-w-full">
                  <span>{item.name}</span>
                  {classifications.includes(item.uuid) ||
                  noClassification.includes(item.uuid) ||
                  item.classes.some((cls) => classes.includes(cls.uuid)) ? (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResetForGroup(
                          item,
                          classifications,
                          classes,
                          noClassification,
                          onValueChange
                        );
                      }}
                    >
                      <CrossIcon />
                    </span>
                  ) : null}
                </div>
              </Accordion.Trigger>
              <Accordion.Content className="sm:t-p-3">
                <ClassificationRadioGroupContent
                  item={item}
                  classifications={classifications}
                  classes={classes}
                  noClassification={noClassification}
                  onValueChange={onValueChange}
                />
              </Accordion.Content>
            </Accordion.Item>
          ))
        ) : (
          <div className="t-flex t-items-center t-h-full t-justify-center">
            No Classifications found
          </div>
        )}
      </Accordion.Root>
    </div>
  );
};
